import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  AUTH_CHANGED_EVENT,
  api,
  clearAuth,
  getStoredUser,
  getToken,
  setAuth,
  setStoredUser,
  setTokens,
  type AuthResponse,
  type User,
} from "./api";
import { AuthContext, type AuthValue, type ExternalSession } from "./AuthContext";
import { offerToSavePassword } from "./credentials";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredUser());
  const [loading, setLoading] = useState<boolean>(Boolean(getToken() && !user));
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { user: me } = await api.me();
      // Persist the fresh user so the cached copy doesn't go stale (e.g.
      // email_verified flips to true after confirming) and survives reloads.
      setStoredUser(me);
      setUser(me);
    } catch (err) {
      // Only drop the session when the server explicitly rejects the token
      // (401/403 — and api.request already tried the refresh token before this
      // bubbles up). A transient failure (offline, 5xx, gateway blip, CORS)
      // must NOT log the user out: keep the cached session so the next request
      // recovers without forcing a re-login. This was the "почему постоянно
      // надо входить" symptom — a flaky /auth/me on load wiped a valid token.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        clearAuth();
        setUser(null);
      } else {
        setUser((cur) => cur ?? getStoredUser());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(id);
  }, [refresh]);

  useEffect(() => {
    const syncStoredUser = () => setUser(getStoredUser());
    window.addEventListener(AUTH_CHANGED_EVENT, syncStoredUser);
    window.addEventListener("storage", syncStoredUser);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncStoredUser);
      window.removeEventListener("storage", syncStoredUser);
    };
  }, []);

  const applyAuthResponse = useCallback((resp: AuthResponse) => {
    setAuth(resp);
    setUser(resp.user);
    return resp.user;
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const resp = await api.login(email, password);
      void offerToSavePassword(email, password);
      return applyAuthResponse(resp);
    },
    [applyAuthResponse],
  );

  const signUp = useCallback(
    async (body: Parameters<typeof api.register>[0]) => {
      const resp = await api.register(body);
      void offerToSavePassword(body.email, body.password, body.display_name);
      return applyAuthResponse(resp);
    },
    [applyAuthResponse],
  );

  const applySession = useCallback(async (session: ExternalSession) => {
    setTokens({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    const { user: me } = await api.me();
    setStoredUser(me);
    setUser(me);
    return me;
  }, []);

  const signOut = useCallback(async () => {
    // Optimistic logout: clear local session immediately so the UI
    // navigates without waiting on the backend (which can be slow).
    // The server-side revoke is best-effort fire-and-forget.
    void api.logout().catch(() => {
      /* ignore — local session is already gone */
    });
    clearAuth();
    setUser(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signOut,
      refresh,
      applySession,
      applyAuthResponse,
      pendingEmail,
      setPendingEmail,
    }),
    [
      user,
      loading,
      signIn,
      signUp,
      signOut,
      refresh,
      applySession,
      applyAuthResponse,
      pendingEmail,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
